import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { api, useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import { serviceBySlug } from "@/data/services";

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString("en-IN", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function Receipt() {
  const { bookingId } = useParams();
  const { user } = useAuth();
  const [receipt, setReceipt] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!user || user === false) return;
    api
      .get(`/bookings/${bookingId}/receipt`)
      .then((r) => setReceipt(r.data))
      .catch(() => setError(true));
  }, [user, bookingId]);

  if (user === null) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center pt-16">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">Loading…</p>
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;

  const service = receipt ? serviceBySlug(receipt.service)?.title || receipt.service : "";

  return (
    <div className="mx-auto max-w-2xl px-4 pb-24 pt-28 sm:px-6" data-testid="receipt-page">
      <Link
        to="/my-bookings"
        data-testid="receipt-back-link"
        className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-blue-900"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        My Bookings
      </Link>

      {error && (
        <p className="mt-8 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="receipt-error">
          Receipt not found, or this booking does not belong to your account.
        </p>
      )}

      {receipt === null && !error && (
        <div className="mt-8 h-64 animate-pulse rounded-xl border border-slate-200 bg-white" data-testid="receipt-loading" />
      )}

      {receipt && (
        <div className="mt-8 rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="receipt-card">
          <div className="border-b border-slate-200 px-8 py-6">
            <p className="font-display text-xl font-bold tracking-tight text-slate-900">NUC Service</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
              National Union Corporation — Payment Receipt
            </p>
          </div>
          <dl className="space-y-0 px-8 py-6 text-sm">
            {[
              ["Booking ID", receipt.booking_ref, "receipt-booking-id"],
              ["Customer name", receipt.name, "receipt-customer"],
              ["Service", service, "receipt-service"],
              ["Amount", receipt.fee_paise > 0 ? `₹${receipt.fee_paise / 100}` : "Free", "receipt-amount"],
              ["Razorpay Payment ID", receipt.payment_id || "—", "receipt-payment-id"],
              ["Razorpay Order ID", receipt.razorpay_order_id || "—", "receipt-order-id"],
              ["Payment date", fmtDateTime(receipt.paid_at), "receipt-date"],
            ].map(([label, value, tid]) => (
              <div key={tid} className="flex justify-between gap-6 border-b border-slate-100 py-3">
                <dt className="text-slate-500">{label}</dt>
                <dd className="text-right font-medium text-slate-900" data-testid={tid}>{value}</dd>
              </div>
            ))}
            <div className="flex items-center justify-between gap-6 border-b border-slate-100 py-3">
              <dt className="text-slate-500">Payment status</dt>
              <dd><StatusBadge value={receipt.payment_status} testId="receipt-payment-status" /></dd>
            </div>
            <div className="flex items-center justify-between gap-6 py-3">
              <dt className="text-slate-500">Booking status</dt>
              <dd><StatusBadge value={receipt.status} testId="receipt-booking-status" /></dd>
            </div>
          </dl>
          <div className="flex flex-wrap gap-3 border-t border-slate-200 px-8 py-6">
            <button
              onClick={() => window.print()}
              data-testid="receipt-print-button"
              className="inline-flex items-center gap-2 rounded-md bg-blue-900 px-5 py-2.5 text-sm font-medium text-white transition-colors duration-200 hover:bg-blue-800"
            >
              <Printer className="h-4 w-4" aria-hidden="true" />
              Print / Save PDF
            </button>
            <Link
              to="/my-bookings"
              data-testid="receipt-my-bookings-link"
              className="rounded-md border-2 border-slate-300 px-5 py-2.5 text-sm font-medium text-slate-700 transition-colors duration-200 hover:border-blue-900 hover:text-blue-900"
            >
              Back to My Bookings
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
